'use client'

import { useState } from 'react'
import Link from 'next/link'
import { Lock, Menu } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet'
import { cn } from '@/lib/utils'
import type { MiniDashboardPage } from './state'

const NAV_ITEMS: { id: MiniDashboardPage; label: string }[] = [
  { id: 'prompts', label: 'Prompts' },
  { id: 'competitors', label: 'Competitors' },
  { id: 'plan', label: 'Plan' },
  { id: 'sources', label: 'Sources' },
  { id: 'visibility', label: 'Visibility' },
]

interface SidebarNavProps {
  activePage: MiniDashboardPage
  onNavigate: (page: MiniDashboardPage) => void
}

function SidebarNav({ activePage, onNavigate }: SidebarNavProps) {
  return (
    <nav role="navigation" aria-label="Dashboard sections" className="flex flex-col gap-0.5 p-2">
      {NAV_ITEMS.map((item) => (
        <button
          key={item.id}
          type="button"
          onClick={() => onNavigate(item.id)}
          aria-current={activePage === item.id ? 'page' : undefined}
          className={cn(
            'w-full rounded-md px-3 py-2 text-left text-sm transition-colors duration-150',
            activePage === item.id
              ? 'bg-foreground text-background font-medium'
              : 'text-muted-foreground hover:bg-muted hover:text-foreground'
          )}
        >
          {item.label}
        </button>
      ))}
    </nav>
  )
}

interface MiniDashboardShellProps {
  activePage: MiniDashboardPage
  onNavigate: (page: MiniDashboardPage) => void
  brandName: string
  domain: string
  lastScannedAt: string
  children: React.ReactNode
}

export function MiniDashboardShell({
  activePage,
  onNavigate,
  brandName,
  domain,
  lastScannedAt,
  children,
}: MiniDashboardShellProps) {
  const [sheetOpen, setSheetOpen] = useState(false)

  function handleMobileNavigate(page: MiniDashboardPage) {
    onNavigate(page)
    setSheetOpen(false)
  }

  return (
    <div
      data-testid="mini-dashboard"
      className="relative rounded-xl border border-border bg-card overflow-hidden"
    >
      {/* Top bar */}
      <div className="flex h-12 items-center gap-3 border-b border-border bg-surface px-3">
        {/* Mobile hamburger */}
        <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
          <SheetTrigger
            render={
              <button
                type="button"
                className="flex items-center justify-center rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground lg:hidden"
                aria-label="Open navigation"
              />
            }
          >
            <Menu className="size-4" />
          </SheetTrigger>
          <SheetContent side="left" className="w-48 p-0 pt-10">
            <SidebarNav activePage={activePage} onNavigate={handleMobileNavigate} />
          </SheetContent>
        </Sheet>

        {/* Brand chip */}
        <span className="rounded-md bg-primary-100 px-2 py-0.5 text-xs font-medium text-primary-900">
          {brandName}
        </span>
        <span className="text-xs text-muted-foreground">{domain}</span>
        <span className="hidden text-xs text-muted-foreground sm:block">
          · Last scan: {lastScannedAt}
        </span>

        {/* Spacer */}
        <div className="flex-1" />

        {/* Sample workspace badge */}
        <span className="hidden items-center gap-1 text-label text-muted-foreground sm:inline-flex">
          <Lock className="size-3" aria-hidden />
          Sample workspace
        </span>
      </div>

      {/* Body: sidebar + main */}
      <div className="flex min-h-[420px]">
        {/* Sidebar — hidden on mobile, visible on lg+ */}
        <aside className="hidden w-32 shrink-0 border-r border-border bg-surface lg:block xl:w-44">
          <SidebarNav activePage={activePage} onNavigate={onNavigate} />
        </aside>

        {/* Main panel */}
        <main
          role="region"
          aria-label={`${activePage} page`}
          className="flex flex-1 flex-col overflow-hidden"
        >
          <div className="flex-1 overflow-hidden p-3">{children}</div>

          {/* CTA footer */}
          <div className="flex items-center justify-end border-t border-border px-3 py-2">
            <Button
              render={<Link href="/auth/login?mode=signup&ref=mini-dashboard" />}
              nativeButton={false}
              size="sm"
              className="shadow-sm text-xs px-3"
            >
              Run this on your domain
            </Button>
          </div>
        </main>
      </div>
    </div>
  )
}
